import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Heading, BodyText, Box } from '@chargify/custom-ui-components';

import {
  Allocation,
  SerializedComponent,
  AllocationsError,
} from '../../machines';

import ComponentsPreview, {
  createComponentsPreviewRow,
} from '../ComponentsPreview';

import { Wrapper, Header } from './ComponentsAllocationEdit.styles';
import { ComponentsAllocationEditSkeleton } from './components';

export type ComponentsAllocationEditProps = {
  components: SerializedComponent[];
  errors?: AllocationsError[];
  onUpdateAllocation: (allocation: Allocation) => void;
};

const ComponentsAllocationEdit = ({
  components,
  errors,
  onUpdateAllocation,
}: ComponentsAllocationEditProps) => {
  const { t, i18n } = useTranslation(['subscriptions-manager']);
  const {
    recurring: { onOff: onOffRecurringRows, quantity: quantityRecurringRows },
    oneTime: { quantity: quantityOneTimeRows },
  } = useMemo(
    () =>
      createComponentsPreviewRow(
        components,
        onUpdateAllocation,
        i18n.language,
        errors
      ),
    [components, i18n.language, errors, onUpdateAllocation]
  );

  const renderRecurringSection =
    !!onOffRecurringRows?.length || !!quantityRecurringRows?.length;

  return (
    <>
      {renderRecurringSection && (
        <>
          <Box marginBottom={4}>
            <Header>
              <Heading variant="h4">
                {t('components_allocations.recurring_addons')}
              </Heading>
              <BodyText variant="body1">
                {t('components_allocations.charged_every_cycle')}
              </BodyText>
            </Header>
          </Box>
          <Box marginBottom={6}>
            <Wrapper gridRowGap={4}>
              {onOffRecurringRows?.length ? (
                <ComponentsPreview rows={onOffRecurringRows} />
              ) : null}
              {quantityRecurringRows?.length ? (
                <ComponentsPreview rows={quantityRecurringRows} />
              ) : null}
            </Wrapper>
          </Box>
        </>
      )}
      {quantityOneTimeRows?.length > 0 && (
        <>
          <Box marginBottom={4}>
            <Header>
              <Heading variant="h4">
                {t('components_allocations.one_time_addons')}
              </Heading>
              <BodyText variant="body1">
                {t('components_allocations.charged_once')}
              </BodyText>
            </Header>
          </Box>
          <Box>
            <Wrapper gridRowGap={4}>
              <ComponentsPreview rows={quantityOneTimeRows} />
            </Wrapper>
          </Box>
        </>
      )}
    </>
  );
};

ComponentsAllocationEdit.LoadingSkeleton = ComponentsAllocationEditSkeleton;

export default ComponentsAllocationEdit;
